import React, { useCallback, useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase.js";
import { toast } from "react-toastify";

function EnvironmentalPage({ isAdmin, isMobile }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [configForm, setConfigForm] = useState({
    latitude: 35.045,
    longitude: -83.625,
    purpleAirSensorIndex: "",
    alertAqiThreshold: 100,
  });

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpsCallable(functions, "getEnvironmentalDashboard")({});
      setDashboard(res.data);
    } catch (err) {
      toast.error(err.message || "Failed to load environmental data");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await httpsCallable(functions, "getEnvironmentalConfig")({});
      const cfg = res.data?.config || {};
      setConfig(cfg);
      setConfigForm({
        latitude: cfg.latitude ?? 35.045,
        longitude: cfg.longitude ?? -83.625,
        purpleAirSensorIndex: cfg.purpleAirSensorIndex ?? "",
        alertAqiThreshold: cfg.alertAqiThreshold ?? 100,
      });
    } catch (err) {
      toast.error(err.message);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadDashboard();
    loadConfig();
  }, [loadDashboard, loadConfig]);

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await httpsCallable(functions, "updateEnvironmentalConfig")({
        ...configForm,
        purpleAirSensorIndex: configForm.purpleAirSensorIndex === "" ? null : Number(configForm.purpleAirSensorIndex),
      });
      toast.success("Environmental config saved");
      loadConfig();
      loadDashboard();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const aqi = dashboard?.airQuality;

  return (
    <div className={`environmental-page ${isMobile ? "environmental-page-mobile" : ""}`}>
      <header className="environmental-hero">
        <p className="environmental-eyebrow">Mountain community</p>
        <h1>Wildfire & Air Quality</h1>
        <p>Live PM2.5 and wildfire risk context for western North Carolina.</p>
      </header>

      {loading ? <p>Loading environmental dashboard…</p> : null}

      {dashboard?.alerts?.length > 0 ? (
        <div className="environmental-alerts">
          {dashboard.alerts.map((alert) => (
            <div key={alert.id} className={`environmental-alert environmental-alert--${alert.severity}`} role="alert">
              {alert.message}
            </div>
          ))}
        </div>
      ) : null}

      {aqi ? (
        <div className="environmental-grid">
          <article className="environmental-card environmental-card--aqi" style={{ borderColor: aqi.color }}>
            <p className="environmental-label">Air quality index</p>
            <p className="environmental-aqi-value" style={{ color: aqi.color }}>{aqi.aqi ?? "—"}</p>
            <p className="environmental-category">{aqi.category}</p>
            <p className="environmental-meta">PM2.5: {aqi.pm25 ?? "—"} µg/m³ · Source: {aqi.source}</p>
            {aqi.humidity != null ? <p className="environmental-meta">Humidity: {aqi.humidity}%</p> : null}
          </article>

          <article className="environmental-card">
            <p className="environmental-label">Wildfire risk</p>
            <p className={`environmental-risk environmental-risk--${dashboard.wildfire?.level}`}>
              {dashboard.wildfire?.label || "—"}
            </p>
            <p>{dashboard.wildfire?.advice}</p>
          </article>
        </div>
      ) : null}

      {dashboard?.links?.length > 0 ? (
        <section className="environmental-links">
          <h2>Official resources</h2>
          <ul>
            {dashboard.links.map((link) => (
              <li key={link.href}>
                <a href={link.href} target="_blank" rel="noopener noreferrer">{link.label}</a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <p className="environmental-disclaimer">{dashboard?.disclaimer}</p>

      <button type="button" className="admin-portal-btn" onClick={loadDashboard} disabled={loading}>
        Refresh readings
      </button>

      {isAdmin ? (
        <section className="environmental-admin">
          <h2>Sensor configuration (admin)</h2>
          <form className="community-services-form" onSubmit={handleSaveConfig}>
            <label>
              Latitude
              <input type="number" step="0.001" value={configForm.latitude} onChange={(e) => setConfigForm((f) => ({ ...f, latitude: e.target.value }))} />
            </label>
            <label>
              Longitude
              <input type="number" step="0.001" value={configForm.longitude} onChange={(e) => setConfigForm((f) => ({ ...f, longitude: e.target.value }))} />
            </label>
            <label>
              PurpleAir sensor index
              <input placeholder="e.g. 12345" value={configForm.purpleAirSensorIndex} onChange={(e) => setConfigForm((f) => ({ ...f, purpleAirSensorIndex: e.target.value }))} />
            </label>
            <label>
              Alert AQI threshold
              <input type="number" value={configForm.alertAqiThreshold} onChange={(e) => setConfigForm((f) => ({ ...f, alertAqiThreshold: e.target.value }))} />
            </label>
            <button type="submit" className="admin-portal-btn admin-portal-btn-primary" disabled={saving}>Save config</button>
          </form>
          {config ? <p className="environmental-meta">Last config load: {config.updatedAt ? "saved" : "defaults"}</p> : null}
        </section>
      ) : null}
    </div>
  );
}

export default EnvironmentalPage;